import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import api, { getFlujoCaja, getCxPAging } from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { 
  FileBarChart, TrendingUp, Calendar, Wallet, Store, 
  Users, PieChart, BarChart3, RefreshCw, Download,
  ArrowUp, ArrowDown, DollarSign, AlertTriangle, CheckCircle2
} from 'lucide-react';

const ReportesPage = () => {
  const { empresaActual } = useApp();
  const [activeTab, setActiveTab] = useState('resultados');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [flujoData, setFlujoData] = useState([]);
  const [agingData, setAgingData] = useState([]);
  const [resultadosData, setResultadosData] = useState(null);
  const [ventasData, setVentasData] = useState(null);
  const [balanceData, setBalanceData] = useState(null);
  const [cxpDetalladoData, setCxpDetalladoData] = useState(null);
  
  // Date range
  const getFirstDayOfMonth = () => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
  };
  
  const [dateRange, setDateRange] = useState({
    inicio: getFirstDayOfMonth(),
    fin: new Date().toISOString().split('T')[0]
  });

  // Format money
  const formatMoney = (amount, showSign = false) => {
    const num = parseFloat(amount) || 0;
    const formatted = Math.abs(num).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (showSign && num !== 0) {
      return num >= 0 ? `+S/. ${formatted}` : `-S/. ${formatted}`;
    }
    return `S/. ${formatted}`;
  };

  // Load Estado de Resultados
  const loadResultados = useCallback(async () => {
    if (!empresaActual?.id) return;
    setLoading(true);
    try {
      const res = await api.get('/reportes/estado-resultados', {
        params: { empresa_id: empresaActual.id, fecha_inicio: dateRange.inicio, fecha_fin: dateRange.fin }
      });
      setResultadosData(res.data);
    } catch (error) {
      toast.error('Error al cargar Estado de Resultados');
    } finally {
      setLoading(false);
    }
  }, [empresaActual?.id, dateRange]);

  // Load Ventas Análisis
  const loadVentas = useCallback(async () => {
    if (!empresaActual?.id) return;
    setLoading(true);
    try {
      const res = await api.get('/reportes/ventas-analisis', {
        params: { empresa_id: empresaActual.id, fecha_inicio: dateRange.inicio, fecha_fin: dateRange.fin }
      });
      setVentasData(res.data);
    } catch (error) {
      toast.error('Error al cargar análisis de ventas');
    } finally {
      setLoading(false);
    }
  }, [empresaActual?.id, dateRange]);

  // Load Balance
  const loadBalance = useCallback(async () => {
    if (!empresaActual?.id) return;
    setLoading(true);
    try {
      const res = await api.get('/reportes/balance-cuentas', {
        params: { empresa_id: empresaActual.id, fecha_corte: dateRange.fin }
      });
      setBalanceData(res.data);
    } catch (error) {
      toast.error('Error al cargar balance');
    } finally {
      setLoading(false);
    }
  }, [empresaActual?.id, dateRange.fin]);

  // Load CxP Detallado
  const loadCxpDetallado = useCallback(async () => {
    if (!empresaActual?.id) return;
    setLoading(true);
    try {
      const res = await api.get('/reportes/cxp-detallado', {
        params: { empresa_id: empresaActual.id }
      });
      setCxpDetalladoData(res.data);
    } catch (error) {
      toast.error('Error al cargar CxP detallado');
    } finally {
      setLoading(false);
    }
  }, [empresaActual?.id]);

  // Load Flujo
  const loadFlujo = useCallback(async () => {
    if (!empresaActual?.id) return;
    setLoading(true);
    try {
      const res = await getFlujoCaja(empresaActual.id, dateRange.inicio, dateRange.fin);
      setFlujoData(res.data);
    } catch (error) {
      toast.error('Error al cargar flujo de caja');
    } finally {
      setLoading(false);
    }
  }, [empresaActual?.id, dateRange]);

  // Load based on active tab
  useEffect(() => {
    const loaders = {
      'resultados': loadResultados,
      'ventas': loadVentas,
      'balance': loadBalance,
      'cxp': loadCxpDetallado,
      'flujo': loadFlujo
    };
    if (loaders[activeTab]) {
      loaders[activeTab]();
    }
  }, [activeTab, loadResultados, loadVentas, loadBalance, loadCxpDetallado, loadFlujo]);

  if (!empresaActual) {
    return <div className="text-center py-12 text-gray-500">Selecciona una empresa para ver los reportes</div>;
  }

  // Calculate totals for flujo
  const totalIngresos = flujoData.filter(f => f.tipo === 'INGRESO').reduce((sum, f) => sum + Math.abs(f.monto_pen), 0);
  const totalEgresos = flujoData.filter(f => f.tipo === 'EGRESO').reduce((sum, f) => sum + Math.abs(f.monto_pen), 0);

  return (
    <div className="space-y-6" data-testid="reportes-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileBarChart className="w-8 h-8 text-indigo-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reportes Avanzados</h1>
            <p className="text-sm text-gray-500">Análisis financiero y operativo</p>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5 h-auto">
          <TabsTrigger value="resultados" className="flex items-center gap-2 py-2">
            <PieChart className="w-4 h-4" />
            <span className="hidden sm:inline">Estado Resultados</span>
          </TabsTrigger>
          <TabsTrigger value="ventas" className="flex items-center gap-2 py-2">
            <Store className="w-4 h-4" />
            <span className="hidden sm:inline">Análisis Ventas</span>
          </TabsTrigger>
          <TabsTrigger value="balance" className="flex items-center gap-2 py-2">
            <Wallet className="w-4 h-4" />
            <span className="hidden sm:inline">Balance Cuentas</span>
          </TabsTrigger>
          <TabsTrigger value="cxp" className="flex items-center gap-2 py-2">
            <AlertTriangle className="w-4 h-4" />
            <span className="hidden sm:inline">CxP Detallado</span>
          </TabsTrigger>
          <TabsTrigger value="flujo" className="flex items-center gap-2 py-2">
            <TrendingUp className="w-4 h-4" />
            <span className="hidden sm:inline">Flujo Caja</span>
          </TabsTrigger>
        </TabsList>

        {/* Filtros de fecha para tabs que los necesitan */}
        {['resultados', 'ventas', 'flujo'].includes(activeTab) && (
          <Card className="mt-4">
            <CardContent className="pt-4">
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <Label>Desde</Label>
                  <Input type="date" value={dateRange.inicio} onChange={(e) => setDateRange({ ...dateRange, inicio: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>Hasta</Label>
                  <Input type="date" value={dateRange.fin} onChange={(e) => setDateRange({ ...dateRange, fin: e.target.value })} className="mt-1" />
                </div>
                <Button onClick={() => {
                  if (activeTab === 'resultados') loadResultados();
                  else if (activeTab === 'ventas') loadVentas();
                  else loadFlujo();
                }} disabled={loading}>
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                  Actualizar
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* TAB: Estado de Resultados */}
        <TabsContent value="resultados" className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-indigo-600" /></div>
          ) : resultadosData ? (
            <>
              {/* Resumen principal */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 text-emerald-600 mb-1">
                      <ArrowUp className="w-4 h-4" />
                      <span className="text-sm">Ingresos (Ventas)</span>
                    </div>
                    <p className="text-2xl font-bold text-emerald-700">{formatMoney(resultadosData.ingresos.total)}</p>
                    <p className="text-xs text-gray-500 mt-1">{resultadosData.ingresos.num_ventas} transacciones</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-red-50 to-white border-red-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 text-red-600 mb-1">
                      <ArrowDown className="w-4 h-4" />
                      <span className="text-sm">Egresos</span>
                    </div>
                    <p className="text-2xl font-bold text-red-700">{formatMoney(resultadosData.egresos.total)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 text-amber-600 mb-1">
                      <Users className="w-4 h-4" />
                      <span className="text-sm">Planilla</span>
                    </div>
                    <p className="text-2xl font-bold text-amber-700">{formatMoney(resultadosData.planilla)}</p>
                  </CardContent>
                </Card>
                <Card className={`bg-gradient-to-br ${resultadosData.utilidad_neta >= 0 ? 'from-blue-50 to-white border-blue-200' : 'from-red-50 to-white border-red-200'}`}>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-1">
                      <DollarSign className={`w-4 h-4 ${resultadosData.utilidad_neta >= 0 ? 'text-blue-600' : 'text-red-600'}`} />
                      <span className={`text-sm ${resultadosData.utilidad_neta >= 0 ? 'text-blue-600' : 'text-red-600'}`}>Utilidad Neta</span>
                    </div>
                    <p className={`text-2xl font-bold ${resultadosData.utilidad_neta >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                      {formatMoney(resultadosData.utilidad_neta)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Margen: {resultadosData.margen_bruto}%</p>
                  </CardContent>
                </Card>
              </div>

              {/* Detalle egresos por categoría */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Egresos por Categoría</CardTitle>
                </CardHeader>
                <CardContent>
                  {resultadosData.egresos.por_categoria.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No hay egresos en el período</p>
                  ) : (
                    <div className="space-y-3">
                      {resultadosData.egresos.por_categoria.map((cat, idx) => {
                        const pct = resultadosData.egresos.total > 0 ? (cat.monto / resultadosData.egresos.total) * 100 : 0;
                        return (
                          <div key={idx} className="flex items-center gap-4">
                            <div className="w-32 truncate text-sm font-medium">{cat.categoria}</div>
                            <Progress value={pct} className="flex-1 h-3" />
                            <div className="w-24 text-right text-sm font-mono">{formatMoney(cat.monto)}</div>
                            <div className="w-12 text-right text-xs text-gray-500">{pct.toFixed(1)}%</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <p className="text-center text-gray-500 py-8">Selecciona un rango de fechas y presiona Actualizar</p>
          )}
        </TabsContent>

        {/* TAB: Análisis de Ventas */}
        <TabsContent value="ventas" className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-indigo-600" /></div>
          ) : ventasData ? (
            <>
              {/* Resumen */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-gray-500">Total Ventas</p>
                    <p className="text-2xl font-bold text-emerald-600">{formatMoney(ventasData.resumen.total_ventas)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-gray-500">Transacciones</p>
                    <p className="text-2xl font-bold text-gray-700">{ventasData.resumen.total_transacciones.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-gray-500">Ticket Promedio</p>
                    <p className="text-2xl font-bold text-indigo-600">{formatMoney(ventasData.resumen.ticket_promedio)}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Ventas por Tienda y Vendedor */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Store className="w-5 h-5 text-indigo-500" />
                      Ventas por Tienda
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tienda</TableHead>
                          <TableHead className="text-right">Ventas</TableHead>
                          <TableHead className="text-right">Monto</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {ventasData.por_tienda.map((t, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{t.tienda}</TableCell>
                            <TableCell className="text-right">{t.num_ventas}</TableCell>
                            <TableCell className="text-right font-mono text-emerald-600">{formatMoney(t.monto)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Users className="w-5 h-5 text-indigo-500" />
                      Top Vendedores
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Vendedor</TableHead>
                          <TableHead className="text-right">Ventas</TableHead>
                          <TableHead className="text-right">Monto</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {ventasData.por_vendedor.map((v, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{v.vendedor}</TableCell>
                            <TableCell className="text-right">{v.num_ventas}</TableCell>
                            <TableCell className="text-right font-mono text-emerald-600">{formatMoney(v.monto)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>

              {/* Por tipo de comprobante */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Por Tipo de Comprobante</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-4">
                    {ventasData.por_tipo.map((t, idx) => (
                      <div key={idx} className="flex items-center gap-3 bg-gray-50 px-4 py-3 rounded-lg">
                        <Badge variant="outline" className="text-sm">{t.tipo}</Badge>
                        <span className="text-sm text-gray-600">{t.num_ventas} ventas</span>
                        <span className="font-mono font-medium text-emerald-600">{formatMoney(t.monto)}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <p className="text-center text-gray-500 py-8">Selecciona un rango de fechas y presiona Actualizar</p>
          )}
        </TabsContent>

        {/* TAB: Balance de Cuentas */}
        <TabsContent value="balance" className="space-y-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-end gap-4">
                <div>
                  <Label>Fecha de Corte</Label>
                  <Input type="date" value={dateRange.fin} onChange={(e) => setDateRange({ ...dateRange, fin: e.target.value })} className="mt-1" />
                </div>
                <Button onClick={loadBalance} disabled={loading}>
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                  Actualizar
                </Button>
              </div>
            </CardContent>
          </Card>

          {balanceData && (
            <>
              <Card className="bg-indigo-50 border-indigo-200">
                <CardContent className="pt-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-indigo-600">Total Saldos (Equivalente PEN)</p>
                      <p className="text-3xl font-bold text-indigo-700">{formatMoney(balanceData.total_saldo_pen)}</p>
                    </div>
                    <p className="text-sm text-gray-500">Al {balanceData.fecha_corte}</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Detalle por Cuenta</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Cuenta</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Moneda</TableHead>
                        <TableHead className="text-right">Saldo Inicial</TableHead>
                        <TableHead className="text-right">Ingresos</TableHead>
                        <TableHead className="text-right">Egresos</TableHead>
                        <TableHead className="text-right">Saldo Final</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {balanceData.cuentas.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell className="font-medium">{c.nombre}</TableCell>
                          <TableCell><Badge variant="outline">{c.tipo}</Badge></TableCell>
                          <TableCell>{c.moneda}</TableCell>
                          <TableCell className="text-right font-mono">{c.simbolo} {c.saldo_inicial.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</TableCell>
                          <TableCell className="text-right font-mono text-emerald-600">{c.simbolo} {c.ingresos.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</TableCell>
                          <TableCell className="text-right font-mono text-red-600">{c.simbolo} {c.egresos.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</TableCell>
                          <TableCell className="text-right font-mono font-bold">{c.simbolo} {c.saldo_final.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* TAB: CxP Detallado */}
        <TabsContent value="cxp" className="space-y-4">
          <Button onClick={loadCxpDetallado} disabled={loading} variant="outline">
            {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Actualizar
          </Button>

          {cxpDetalladoData && (
            <>
              {/* Resumen por antigüedad */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card className="border-green-200 bg-green-50">
                  <CardContent className="pt-4 text-center">
                    <CheckCircle2 className="w-6 h-6 text-green-500 mx-auto mb-1" />
                    <p className="text-xs text-gray-500">Vigente</p>
                    <p className="text-lg font-bold text-green-700">{formatMoney(cxpDetalladoData.resumen.vigente.total)}</p>
                    <p className="text-xs text-gray-400">{cxpDetalladoData.resumen.vigente.count} docs</p>
                  </CardContent>
                </Card>
                <Card className="border-yellow-200 bg-yellow-50">
                  <CardContent className="pt-4 text-center">
                    <p className="text-xs text-gray-500">1-30 días</p>
                    <p className="text-lg font-bold text-yellow-700">{formatMoney(cxpDetalladoData.resumen.rango_1_30.total)}</p>
                    <p className="text-xs text-gray-400">{cxpDetalladoData.resumen.rango_1_30.count} docs</p>
                  </CardContent>
                </Card>
                <Card className="border-orange-200 bg-orange-50">
                  <CardContent className="pt-4 text-center">
                    <p className="text-xs text-gray-500">31-60 días</p>
                    <p className="text-lg font-bold text-orange-700">{formatMoney(cxpDetalladoData.resumen.rango_31_60.total)}</p>
                    <p className="text-xs text-gray-400">{cxpDetalladoData.resumen.rango_31_60.count} docs</p>
                  </CardContent>
                </Card>
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="pt-4 text-center">
                    <p className="text-xs text-gray-500">61-90 días</p>
                    <p className="text-lg font-bold text-red-700">{formatMoney(cxpDetalladoData.resumen.rango_61_90.total)}</p>
                    <p className="text-xs text-gray-400">{cxpDetalladoData.resumen.rango_61_90.count} docs</p>
                  </CardContent>
                </Card>
                <Card className="border-red-400 bg-red-100">
                  <CardContent className="pt-4 text-center">
                    <AlertTriangle className="w-6 h-6 text-red-600 mx-auto mb-1" />
                    <p className="text-xs text-gray-500">&gt;90 días</p>
                    <p className="text-lg font-bold text-red-800">{formatMoney(cxpDetalladoData.resumen.mayor_90.total)}</p>
                    <p className="text-xs text-gray-400">{cxpDetalladoData.resumen.mayor_90.count} docs</p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="flex justify-between items-center">
                    <span>Total Pendiente</span>
                    <span className="text-2xl text-orange-600">{formatMoney(cxpDetalladoData.total_pendiente)}</span>
                  </CardTitle>
                </CardHeader>
              </Card>
            </>
          )}
        </TabsContent>

        {/* TAB: Flujo de Caja */}
        <TabsContent value="flujo" className="space-y-4">
          {/* Resumen */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-gray-500">Total Ingresos</p>
                <p className="text-2xl font-bold text-emerald-600">{formatMoney(totalIngresos)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-gray-500">Total Egresos</p>
                <p className="text-2xl font-bold text-red-600">{formatMoney(totalEgresos)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-gray-500">Neto</p>
                <p className={`text-2xl font-bold ${(totalIngresos - totalEgresos) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatMoney(totalIngresos - totalEgresos)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Detalle */}
          <Card>
            <CardHeader>
              <CardTitle>Movimientos</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-indigo-600" /></div>
              ) : flujoData.length === 0 ? (
                <div className="text-center py-12 text-gray-500">No hay movimientos en el período</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Descripción</TableHead>
                      <TableHead className="text-right">Monto</TableHead>
                      <TableHead className="text-right">Saldo Acum.</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {flujoData.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{item.fecha}</TableCell>
                        <TableCell>
                          <Badge className={item.tipo === 'INGRESO' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                            {item.tipo}
                          </Badge>
                        </TableCell>
                        <TableCell>{item.descripcion || '-'}</TableCell>
                        <TableCell className={`text-right font-mono ${item.monto_pen >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {formatMoney(item.monto_pen)}
                        </TableCell>
                        <TableCell className="text-right font-mono font-bold">
                          {formatMoney(item.saldo_acum)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportesPage;
